import flattenDeep from 'lodash/flattenDeep';
import { List, AutoSizer, CellMeasurer, CellMeasurerCache } from 'react-virtualized';
import React, { Component, Fragment } from 'react';
import type { ListRowProps } from 'react-virtualized';

import {
    TranscriptVirtualScrollWrapper,
    Header,
    ToggleButton,
    Wrapper,
    TranscriptItem,
    TranscriptWrapper,
    TranscriptItemWrapper
} from './styled/TrainerTranscriptStyled';

import WordInformation from './WordInformation/TrainerTranscriptWordInformation';

import CopyButton from './CopyButton/TrainerTranscriptCopyButton';
import Icon from '../../common/Icon';
import AutoScroll from '../../common/AutoScroll';
import {
    BlacklistPhraseItem,
    CombinedTranscriptItem,
    combineTimelineTranscript,
    getTranscriptString,
    PhraseItem,
    TranscriptItem as SourceTranscriptItem,
    TranscriptRenderChunk,
    TranscriptRenderWord
} from '../../../utils/transcript';
import { IS_ELECTRON_APP, IS_UI_ONLY_MODE } from '../../../config/electron';

const VIRTUAL_SCROLLING_THRESHOLD = 200;

interface TranscriptProps {
    transcript: SourceTranscriptItem[];
    onToggle: (isVisible: boolean) => void;
    isVisible?: boolean;
    localization: { getText: (key: string, options?: Record<string, unknown>) => string };
    whitelist: PhraseItem[];
    blacklist: BlacklistPhraseItem[];
    copyButtonEnabled?: boolean;
    onCopyButtonClick: () => void;
    showMicrophoneTranscriptOnly: boolean;
    copyToClipboard: (payload: string) => void;
    showConfidence?: boolean;
    showPronunciation?: boolean;
    displayTranscript?: boolean;
    isCallStarted: boolean;
    dataTestId?: string;
}

class Transcript extends Component<TranscriptProps> {
    static defaultProps: Pick<TranscriptProps, 'transcript' | 'showConfidence' | 'showPronunciation' | 'displayTranscript' | 'isVisible'> =
        {
            transcript: [],
            showConfidence: false,
            showPronunciation: false,
            displayTranscript: false,
            isVisible: false
        };

    virtualScrollSizesCache = new CellMeasurerCache({
        fixedWidth: true,
        minHeight: 50
    });

    shouldComponentUpdate(nextProps: TranscriptProps) {
        return (
            nextProps.isCallStarted !== this.props.isCallStarted ||
            nextProps.isVisible !== this.props.isVisible ||
            nextProps.transcript.length !== this.props.transcript.length ||
            nextProps.copyButtonEnabled !== this.props.copyButtonEnabled ||
            nextProps.displayTranscript !== this.props.displayTranscript
        );
    }

    _copyText = (timeTranscript: { transcript: { text: string }[]; type: string }[]) => {
        const { copyToClipboard, copyButtonEnabled, onCopyButtonClick, localization } = this.props;

        if (!copyButtonEnabled) return;

        onCopyButtonClick();
        copyToClipboard(getTranscriptString(timeTranscript, localization));
    };

    _toggleOpen = () => {
        const { isVisible, onToggle } = this.props;

        onToggle(!isVisible);
    };

    _renderToggleButton = () => {
        const { isVisible } = this.props;

        return (
            <ToggleButton $isVisible={Boolean(isVisible)}>
                <Icon name="Triangle" size={10} />
            </ToggleButton>
        );
    };

    _renderHeader = () => {
        const { localization, isVisible } = this.props;

        return (
            <Header
                data-testid={this.props.dataTestId ? `${this.props.dataTestId}-header` : undefined}
                onClick={this._toggleOpen}
                $rounded={IS_UI_ONLY_MODE && !IS_ELECTRON_APP}>
                {isVisible
                    ? localization.getText('trainer.transcript.expand_button.hide')
                    : localization.getText('trainer.transcript.expand_button.show')}
                {this._renderToggleButton()}
            </Header>
        );
    };

    _renderWord = (word: TranscriptRenderWord, key: string) => {
        const { showConfidence, showPronunciation, localization } = this.props;

        return (
            <WordInformation
                key={key}
                pronunciation={word.pronunciation}
                confidence={word.confidence}
                showConfidence={Boolean(showConfidence)}
                showPronunciation={Boolean(showPronunciation)}
                localization={localization}
                word={word}
            />
        );
    };

    _renderDetailedTranscript = (parsedTranscript: TranscriptRenderChunk[]) => {
        return flattenDeep(
            parsedTranscript.map((list: TranscriptRenderChunk, idx: number) => {
                return list.render.map((word: TranscriptRenderWord, i: number) => {
                    return this._renderWord(word, `${list.text}_${idx}_${i}`);
                });
            })
        );
    };

    _renderTranscriptItems = (timeTranscript: CombinedTranscriptItem[]) => {
        return timeTranscript.map((item: CombinedTranscriptItem) => {
            const detectedTranscript = this._renderDetailedTranscript(item.transcript);
            return (
                <TranscriptItem data-testid="TranscriptItem" className={item.type} key={`${item.type}_${item.startOffset}`}>
                    {detectedTranscript}
                </TranscriptItem>
            );
        });
    };

    _renderCompleteTranscript = (timeTranscript: CombinedTranscriptItem[]) => {
        const { isVisible, showMicrophoneTranscriptOnly, isCallStarted } = this.props;

        if (!isVisible) return null;

        return (
            <AutoScroll startAtBottom={isCallStarted}>
                <TranscriptWrapper $showMicrophoneTranscriptOnly={showMicrophoneTranscriptOnly}>
                    {this._renderTranscriptItems(timeTranscript)}
                </TranscriptWrapper>
            </AutoScroll>
        );
    };

    _clearLastTranscriptSizesCache(transcriptLength: number, rowCount: number) {
        for (let i = 1; i <= rowCount; i++) {
            this.virtualScrollSizesCache.clear(transcriptLength - i, 0);
        }
    }

    _renderRow(
        timeTranscript: CombinedTranscriptItem[],
        { index, key, parent, style }: { index: number; key: string; parent: ListRowProps['parent']; style: React.CSSProperties }
    ) {
        const item = timeTranscript[index];

        const detectedTranscript = this._renderDetailedTranscript(item.transcript);
        return (
            <CellMeasurer key={key} cache={this.virtualScrollSizesCache} parent={parent} columnIndex={0} rowIndex={index}>
                <TranscriptItemWrapper style={{ ...style }}>
                    <TranscriptItem data-testid="TranscriptItem" className={item.type} key={`${item.type}_${item.startOffset}`}>
                        {detectedTranscript}
                    </TranscriptItem>
                </TranscriptItemWrapper>
            </CellMeasurer>
        );
    }

    _renderVirtualScrollTranscript = (timeTranscript: CombinedTranscriptItem[]) => {
        const { isVisible, showMicrophoneTranscriptOnly, isCallStarted } = this.props;

        if (!isVisible) return null;

        this._clearLastTranscriptSizesCache(timeTranscript.length, 3);

        return (
            <TranscriptVirtualScrollWrapper $showMicrophoneTranscriptOnly={showMicrophoneTranscriptOnly}>
                <AutoSizer>
                    {({ width, height }: { width: number; height: number }) => {
                        return (
                            <List
                                width={width}
                                height={height}
                                deferredMeasurementCache={this.virtualScrollSizesCache}
                                rowHeight={this.virtualScrollSizesCache.rowHeight}
                                rowRenderer={(data: ListRowProps) => {
                                    return this._renderRow(timeTranscript, data);
                                }}
                                rowCount={timeTranscript.length}
                                scrollToIndex={isCallStarted ? timeTranscript.length - 1 : 0}
                                scrollToAlignment="end"
                            />
                        );
                    }}
                </AutoSizer>
            </TranscriptVirtualScrollWrapper>
        );
    };

    _renderTranscriptPopup = () => {
        const { isVisible, localization, copyButtonEnabled, transcript, whitelist, blacklist } = this.props;

        const timeTranscript = combineTimelineTranscript(transcript, whitelist, blacklist);

        return (
            <Wrapper data-testid={this.props.dataTestId} $isVisible={Boolean(isVisible)} $rounded={IS_UI_ONLY_MODE && !IS_ELECTRON_APP}>
                {isVisible && (
                    <CopyButton
                        onCopy={() => this._copyText(timeTranscript)}
                        enabled={Boolean(copyButtonEnabled)}
                        localization={localization}
                    />
                )}
                {transcript.length < VIRTUAL_SCROLLING_THRESHOLD
                    ? this._renderCompleteTranscript(timeTranscript)
                    : this._renderVirtualScrollTranscript(timeTranscript)}
            </Wrapper>
        );
    };

    render() {
        const { displayTranscript } = this.props;

        if (!displayTranscript) return null;

        return (
            <Fragment>
                {this._renderHeader()}
                {this._renderTranscriptPopup()}
            </Fragment>
        );
    }
}

export default Transcript;
